import { IS_DOMAIN_REWRITE_ENABLED } from '@/app-shell/flags/shell-flags';
import { getOnPremApiBaseUrl, getOnPremWebSocketUrl } from '@/tauri/onprem/serverConfig';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import type { ENV_NAME } from '@heidi/api/config';
import {
  AZURE_DEV_ENDPOINT,
  AZURE_DEV_WS_ENDPOINT,
  createApiConfig,
  DEV_ENDPOINT,
  DEV_WS_ENDPOINT,
  LOCALHOST_ENDPOINT,
  LOCALHOST_WS_ENDPOINT,
  PREVIEW_HOSTNAME,
  scribeDomains,
  scribeWebsocketDomains,
  STAGING_ENDPOINT,
  STAGING_WS_ENDPOINT,
  TRANSCRIPTION_PREVIEW_HOSTNAME,
  type Region as ApiConfigRegion,
} from '@heidi/api/config';
import { originalConsoleMethods } from '@heidi/runtime/logger';
import type { Region } from '@heidi/schema/ml-scribe';

/** Literal NEXT_PUBLIC_* reads in app code so Next.js inlines them in the client bundle. */
const readNextPublicEnv = (key: string): string | undefined => {
  switch (key) {
    case 'NEXT_PUBLIC_API_BASE_URL':
      return process.env.NEXT_PUBLIC_API_BASE_URL;
    case 'NEXT_PUBLIC_WS_BASE_URL':
      return process.env.NEXT_PUBLIC_WS_BASE_URL;
    case 'NEXT_PUBLIC_WIDGET_EHR_BASE_URL':
      return process.env.NEXT_PUBLIC_WIDGET_EHR_BASE_URL;
    default:
      return process.env[key];
  }
};

const apiConfig = createApiConfig({
  isOnPrem,
  isDomainRewriteEnabled: () => Boolean(IS_DOMAIN_REWRITE_ENABLED()),
  readEnv: readNextPublicEnv,
  // Tauri desktop on-prem: resolve API/WS base URLs from the user-configured
  // server URL in localStorage. Web builds ignore these (window.location.origin
  // is correct). The getters are only wired when isTauri() to avoid importing
  // localStorage access in cloud builds.
  ...(isOnPrem &&
    isTauri() && {
      getOnPremServerUrl: getOnPremApiBaseUrl,
      getOnPremWebSocketUrl: getOnPremWebSocketUrl,
    }),
});

export {
  AZURE_DEV_ENDPOINT,
  AZURE_DEV_WS_ENDPOINT,
  DEV_ENDPOINT,
  DEV_WS_ENDPOINT,
  LOCALHOST_ENDPOINT,
  LOCALHOST_WS_ENDPOINT,
  PREVIEW_HOSTNAME,
  scribeDomains,
  scribeWebsocketDomains,
  STAGING_ENDPOINT,
  STAGING_WS_ENDPOINT,
  TRANSCRIPTION_PREVIEW_HOSTNAME,
};

export const getMlScribePreviewConfig = (region: Region, environment: ENV_NAME) =>
  apiConfig.getMlScribePreviewConfig(region as ApiConfigRegion, environment);

export const getTranscriptionPreviewParam = (region: Region, environment: ENV_NAME) =>
  apiConfig.getTranscriptionPreviewParam(region as ApiConfigRegion, environment);

const PREVIEW_BANNER_BADGE_STYLE =
  'background: #ff6b00; color: white; font-size: 14px; font-weight: bold; padding: 8px 12px; border-radius: 4px;';
const PREVIEW_BANNER_TEXT_STYLE = 'color: #ff6b00; font-weight: bold;';

/**
 * Once per page session. React Strict Mode double-invokes effects in dev;
 * without this gate the orange badges would print twice.
 */
let previewEnvironmentBannersPrinted = false;

/**
 * Capture `?ml-scribe=` / `?transcription=` for the page session and print the
 * styled console badges via the *native* console (bypassing Datadog's
 * interceptor, which otherwise turns `%c` banners into pino debug objects).
 */
export const initPreviewEnvironmentBanners = (region: Region) => {
  const { mlScribe, transcription } = apiConfig.resolvePreviewEnvironmentParams(
    region as ApiConfigRegion
  );

  if (previewEnvironmentBannersPrinted) return;
  // Only flip the gate when we actually print — a no-param init must not
  // permanently suppress a later init that has preview params.
  if (!mlScribe && !transcription) return;

  previewEnvironmentBannersPrinted = true;

  if (mlScribe) {
    originalConsoleMethods.log(
      `%c 🔧 PREVIEW ENVIRONMENT ACTIVE 🔧 %c\n\nBackend: ${getUrlFromConfig(region)}\n`,
      PREVIEW_BANNER_BADGE_STYLE,
      PREVIEW_BANNER_TEXT_STYLE
    );
  }

  if (transcription) {
    originalConsoleMethods.log(
      `%c 🔧 TRANSCRIPTION PREVIEW ENVIRONMENT ACTIVE 🔧 %c\n\nInstance: ${transcription}\nHost: https://${TRANSCRIPTION_PREVIEW_HOSTNAME}\n`,
      PREVIEW_BANNER_BADGE_STYLE,
      PREVIEW_BANNER_TEXT_STYLE
    );
  }
};

export const getUrlFromConfig = (region: Region) =>
  apiConfig.getUrlFromConfig(region as ApiConfigRegion);

export const getWebsocketUrlFromConfig = (region: Region) =>
  apiConfig.getWebsocketUrlFromConfig(region as ApiConfigRegion);

export const handleDomainRewrite = apiConfig.handleDomainRewrite;
export const getWidgetEhrBaseUrl = apiConfig.getWidgetEhrBaseUrl;
